import { format, sub } from "date-fns";

export interface DateRangeValue {
  from: Date;
  to: Date;
  previousFrom: Date;
  previousTo: Date;
}

export interface QuickRange {
  key: string;
  label: string;
}

export const DATE_RANGE_KEYS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_2_DAYS: 'last2days',
  LAST_7_DAYS: 'last7days',
  LAST_30_DAYS: 'last30days',
  LAST_90_DAYS: 'last90days',
  LAST_6_MONTHS: 'last6months',
  LAST_YEAR: 'lastyear',
  THIS_WEEK: 'thisweek',
  THIS_MONTH: 'thismonth',
  THIS_QUARTER: 'thisquarter',
  THIS_YEAR: 'thisyear',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  YEARLY: 'yearly',
  LAST_4_WEEKS: 'last4weeks',
  LAST_4_QUARTERS: 'last4quarters',
  LAST_3_YEARS: 'last3years',
  LAST_QUARTERS: 'lastquarter',
  CUSTOM: 'custom',
} as const;

/** Preset mặc định: Yesterday, Last 2 days, Last 7 days, Last 30 days (không có From/To). */
export const DEFAULT_QUICK_RANGES: QuickRange[] = [
  { key: DATE_RANGE_KEYS.TODAY, label: "Today" },
  { key: DATE_RANGE_KEYS.YESTERDAY, label: "Yesterday" },
  { key: DATE_RANGE_KEYS.LAST_2_DAYS, label: "Last 2 days" },
  { key: DATE_RANGE_KEYS.LAST_7_DAYS, label: "Last 7 days" },
  { key: DATE_RANGE_KEYS.LAST_30_DAYS, label: "Last 30 days" },
];

export const PERIODIC_QUICK_RANGES: QuickRange[] = [
  { key: DATE_RANGE_KEYS.WEEKLY, label: "Weekly" },
  { key: DATE_RANGE_KEYS.MONTHLY, label: "Monthly" },
  { key: DATE_RANGE_KEYS.QUARTERLY, label: "Quarterly" },
  { key: DATE_RANGE_KEYS.YEARLY, label: "Yearly" },
];

function getDateRangeFromKey(key: string): { from: Date; to: Date } {
  const now = new Date();
  let from = now;
  let to = now;

  switch (key) {
    case DATE_RANGE_KEYS.TODAY:
      from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case DATE_RANGE_KEYS.YESTERDAY:
      from = sub(now, { days: 1 });
      to = sub(now, { days: 1 });
      break;
    case DATE_RANGE_KEYS.LAST_2_DAYS:
      to = now;
      from = sub(now, { days: 2 });
      break;
    case DATE_RANGE_KEYS.LAST_7_DAYS:
      to = now;
      from = sub(now, { days: 7 });
      break;
    case DATE_RANGE_KEYS.LAST_30_DAYS:
      to = now;
      from = sub(now, { days: 30 });
      break;
    case DATE_RANGE_KEYS.LAST_90_DAYS:
      to = now;
      from = sub(now, { days: 90 });
      break;
    case DATE_RANGE_KEYS.LAST_6_MONTHS:
      to = now;
      from = sub(now, { months: 6 });
      break;
    case DATE_RANGE_KEYS.LAST_YEAR:
      from = new Date(now.getFullYear() - 1, 0, 1); // January 1st of last year
      to = new Date(now.getFullYear() - 1, 11, 31); // December 31st of last year
      break;
    case DATE_RANGE_KEYS.THIS_WEEK:
      const day = now.getDay();
      const diff = now.getDate() - day; // adjust to start from Sunday
      from = new Date(now.getFullYear(), now.getMonth(), diff);
      to = new Date(now.getFullYear(), now.getMonth(), diff + 6);
      break;
    case DATE_RANGE_KEYS.THIS_MONTH:
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      to = now;
      break;
    case DATE_RANGE_KEYS.THIS_QUARTER:
      const currentQuarter = Math.floor(now.getMonth() / 3);
      from = new Date(now.getFullYear(), currentQuarter * 3, 1);
      to = now;
      break;
    case DATE_RANGE_KEYS.THIS_YEAR:
      from = new Date(now.getFullYear(), 0, 1); // 1/1 of the current year
      to = now;
      break;
    case DATE_RANGE_KEYS.WEEKLY:
    case DATE_RANGE_KEYS.LAST_4_WEEKS:
      to = now;
      // Get the current day of week (0 = Sunday, 6 = Saturday)
      const currentDay = now.getDay();
      // Calculate days to subtract to get to the start of the week (Sunday)
      const daysToSubtract = currentDay;
      // Set from date to the start of the current week (Sunday)
      from = sub(now, { days: daysToSubtract });
      from = sub(from, { weeks: 3 });
      break;
    case DATE_RANGE_KEYS.MONTHLY:
      to = now;
      from = new Date(now.getFullYear(), now.getMonth() - 5, 1); // 1st day of 6 months ago
      break;
    case DATE_RANGE_KEYS.QUARTERLY:
      to = now;
      // Calculate current quarter (0-3)
      const currentQuarterIndex = Math.floor(now.getMonth() / 3);
      // Go back 3 quarters
      const targetQuarter = currentQuarterIndex - 3;
      // Calculate year and start month of the target quarter
      const targetYear = now.getFullYear() + Math.floor(targetQuarter / 4);
      const targetMonth = ((targetQuarter % 4) + 4) % 4 * 3; // Ensure month is not negative
      from = new Date(targetYear, targetMonth, 1);
      break;
    case DATE_RANGE_KEYS.YEARLY:
    case DATE_RANGE_KEYS.LAST_3_YEARS:
      to = now;
      from = new Date(now.getFullYear() - 2, 0, 1); // January 1st of 3 years ago
      break;
  }

  // Ensure to date doesn't exceed today
  const limitDate = new Date();
  if (to > limitDate) {
    to = limitDate;
  }

  // Set time to start of day (00:00:00.000) for from date
  from.setHours(0, 0, 0, 0);
  // Set time to end of day (23:59:59.999) for to date
  if (key !== DATE_RANGE_KEYS.TODAY) {
    to.setHours(23, 59, 59, 999);
  }

  return { from, to };
}

function calculatePreviousDateRange(from: Date, to: Date, key: string): { previousFrom: Date; previousTo: Date } {
  if (key === DATE_RANGE_KEYS.TODAY) {
    return {
      previousFrom: sub(from, { days: 1 }),
      previousTo: sub(to, { days: 1 }),
    };
  }

  let previousFrom: Date;
  let previousTo: Date;

  // For other ranges, calculate based on the number of days
  const rangeMs = to.getTime() - from.getTime();
  const daysInRange = Math.floor(rangeMs / (24 * 60 * 60 * 1000)) + 1;

  previousTo = new Date(to);

  // First move back one week to ensure we're outside the range
  previousTo = new Date(previousTo.getTime() - 7 * 24 * 60 * 60 * 1000);

  // Keep moving back weeks until we're completely outside the range
  while (previousTo >= from) {
    previousTo = new Date(previousTo.getTime() - 7 * 24 * 60 * 60 * 1000);
  }

  // Calculate previousFrom by subtracting (daysInRange - 1) days from previousTo
  previousFrom = new Date(previousTo.getTime() - (daysInRange - 1) * 24 * 60 * 60 * 1000);

  // Set time to start of day (00:00:00.000) for previousFrom date
  previousFrom.setHours(0, 0, 0, 0);
  // Set time to end of day (23:59:59.999) for previousTo date
  previousTo.setHours(23, 59, 59, 999);

  return { previousFrom, previousTo };
}

export function calculateDateRange(key: string): DateRangeValue {
  const { from, to } = getDateRangeFromKey(key);
  const { previousFrom, previousTo } = calculatePreviousDateRange(from, to, key);
  return {
    from,
    to,
    previousFrom,
    previousTo
  };
}

export function formatDateRange(dateRange: DateRangeValue): {
  from: string;
  to: string;
  previousFrom: string;
  previousTo: string;
} {
  return {
    from: format(dateRange.from, "yyyy-MM-dd"),
    to: format(dateRange.to, "yyyy-MM-dd"),
    previousFrom: format(dateRange.previousFrom, "yyyy-MM-dd"),
    previousTo: format(dateRange.previousTo, "yyyy-MM-dd")
  };
}

export function calculateCustomDateRange(from: Date, to: Date): DateRangeValue {
  // Set time to start of day (00:00:00.000) for from date
  from.setHours(0, 0, 0, 0);
  // Set time to end of day (23:59:59.999) for to date
  to.setHours(23, 59, 59, 999);

  const { previousFrom, previousTo } = calculatePreviousDateRange(from, to, 'custom');

  return {
    from,
    to,
    previousFrom,
    previousTo
  };
}

const customRange = calculateCustomDateRange(new Date('2024-01-01'), new Date('2024-01-31'));

/**
 * Recalculates date range for "Today so far" if needed, otherwise returns the cached dates.
 * This ensures that "Today so far" always has the current time as endDate when switching tabs.
 * 
 * @param dateRangeKey - The date range key (e.g., 'today', 'yesterday', etc.)
 * @param cachedDates - The cached dates from filters
 * @returns Object with startDate, endDate, previousStartDate, previousEndDate in ISO format
 */
export function getRecalculatedDatesIfNeeded(
  dateRangeKey: string,
  cachedDates: {
    startDate: string;
    endDate: string;
    previousStartDate: string;
    previousEndDate: string;
  }
): {
  startDate: string;
  endDate: string;
  previousStartDate: string;
  previousEndDate: string;
} {
  // List of dynamic date ranges that should always be recalculated
  const dynamicDateRanges = [
    DATE_RANGE_KEYS.TODAY,
    DATE_RANGE_KEYS.THIS_WEEK,
    DATE_RANGE_KEYS.THIS_MONTH,
    DATE_RANGE_KEYS.THIS_QUARTER,
    DATE_RANGE_KEYS.THIS_YEAR,
    DATE_RANGE_KEYS.WEEKLY,
    DATE_RANGE_KEYS.MONTHLY,
    DATE_RANGE_KEYS.QUARTERLY,
    DATE_RANGE_KEYS.YEARLY,
  ];

  // Recalculate dates for dynamic date ranges to ensure endtime is current
  if (dynamicDateRanges.includes(dateRangeKey as (typeof dynamicDateRanges)[number])) {
    const dateRange = calculateDateRange(dateRangeKey);
    return {
      startDate: dateRange.from.toISOString(),
      endDate: dateRange.to.toISOString(),
      previousStartDate: dateRange.previousFrom.toISOString(),
      previousEndDate: dateRange.previousTo.toISOString(),
    };
  }

  // For fixed date ranges (Yesterday, Last Week, Custom), use cached values
  return cachedDates;
}